/**
 * Quick view slider
 * @init
 * @param {array} files - an array of files to be displayed in the preview
 */

/**
 * Function to open Quick view slider
 * @openSlider
 * @param {string} id - id to open the particular file
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/quick-view-slider/quick-view-slider.css" />
 */

import Tooltip from "/WebInterface/new-ui/modules/tooltip/tooltip.js";
import MediaBar from "/WebInterface/new-ui/modules/quick-view-slider/media-bar/media-bar.js";
import {
  AudioPreview,
  ImagePreview,
  VideoPreview,
} from "/WebInterface/new-ui/modules/quick-view-slider/media.js";
import store from "/WebInterface/new-ui/store/index.js";
import {
  mediaTypes,
  videoExtensionsSlideshow,
  audioExtensionsSlideshow,
} from "/WebInterface/new-ui/assets/js/constants.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import userActions from "/WebInterface/new-ui/assets/js/app/user-actions.js";
import templates from "./template.js";

let id_counter = 0;

export default class QuickViewSlider {
  constructor() {
    this.activeIndex = 0;
    this.mediaArray = [];
    this.mediaArrayLength = this.mediaArray.length;
    this.id = `quick-view-slider-${++id_counter}`;
    this.filesAddedToBasket = new Set([]);
    this.tooltip = new Tooltip();
    this.media;
    this.scrollHandler = this.scrollHandler.bind(this);
    this.zoomFactor = 1;
    this.minZoom = 0.1;
    this.maxZoom = 5;
  }

  init = () => {
    const existingSlide = _$(`#${this.id}`);
    if (!existingSlide.el()) {
      this.createSlider();
    }
  };

  createSlider = () => {
    const mainSlider = _$("<div>").addClass("quick-view-slider");
    _$("body").append(mainSlider.el());
    mainSlider.el().id = this.id;

    mainSlider.html(templates.slideShowTemplate());

    const quickViewClose = _$(`#${this.id} .quick-view-close`);
    quickViewClose.on("click", this.closeSlider.bind(this));
  };

  getMediaInfo = (sliderInfo) => {
    const mediaInfo = sliderInfo?.mediaInfo;
    if (mediaInfo) return mediaInfo;

    const fileExtension = utils.getFileExtension(sliderInfo);
    const canPlayVideo = document
      .createElement("video")
      ?.canPlayType(`video/${fileExtension}`);
    const canPlayAudio = document
      .createElement("audio")
      ?.canPlayType(`audio/${fileExtension}`);

    const getMediaInfoObj = (type, extension) => {
      return {
        type: type,
        mimeType: `${type}/${extension}`,
      };
    };

    if (canPlayVideo || videoExtensionsSlideshow.includes(fileExtension))
      return getMediaInfoObj("video", fileExtension);
    if (canPlayAudio || audioExtensionsSlideshow.includes(fileExtension))
      return getMediaInfoObj("audio", fileExtension);
    return getMediaInfoObj("image", fileExtension);
  };

  displayFile = (sliderInfo) => {
    this.zoomFactor = 1;
    this.videoPreview = null;
    this.imagePreview = null;
    this.audioPreview = null;

    const { name } = (this.sliderInfo = sliderInfo);

    const sliderPageCounter = _$(`#${this.id} .quick-view-pageTotal`);
    const content = _$(`#${this.id} .quick-view-player-section`).el();

    _$(content).html("");
    sliderPageCounter.html(
      `<p>${this.activeIndex + 1} / ${this.mediaArrayLength}</p>`
    );

    const mediaInfo = this.getMediaInfo(sliderInfo);

    switch (mediaInfo?.type) {
      case mediaTypes.IMAGE:
        this.renderImageQuickView(sliderInfo);
        break;

      case mediaTypes.VIDEO:
        this.renderVideoQuickView(sliderInfo);
        break;

      case mediaTypes.AUDIO:
        this.renderAudioQuickView(sliderInfo);
        break;

      default:
        this.renderImageQuickView({
          ...sliderInfo,
          mediaInfo: {
            mimeType: "image/jpg",
            type: "image",
          },
        });
        break;
    }

    const nameBar = _$(`#${this.id} .name-bar`);
    nameBar.text(name);
    this.tooltip.removeTooltip();
  };

  openSlider = (id) => {
    this.init();
    this.mediaArrayLength = this.mediaArray.length;

    if (id) {
      const file = this.mediaArray.find((file) => file.id === id);
      this.activeIndex = this.mediaArray.indexOf(file);
    } else {
      this.activeIndex = 0;
    }

    if (!this.mediaArray[this.activeIndex]) return;

    const sliderInfo = this.mediaArray[this.activeIndex];
    this.displayFile(sliderInfo);

    window.addEventListener("keyup", this.keyUpHandler);
    _$("html").addClass("crushftp-opened-modal");
  };

  previous = () => {
    this.activeIndex =
      this.activeIndex === 0 ? this.mediaArrayLength - 1 : this.activeIndex - 1;
    const sliderInfo = this.mediaArray[this.activeIndex];
    this.displayFile(sliderInfo);
  };

  next = () => {
    this.activeIndex =
      this.activeIndex === this.mediaArrayLength - 1 ? 0 : this.activeIndex + 1;
    const sliderInfo = this.mediaArray[this.activeIndex];
    this.displayFile(sliderInfo);
  };

  renderImageQuickView = (sliderInfo) => {
    const content = _$(`#${this.id} .quick-view-player-section`).el();
    const { mediaInfo } = sliderInfo;

    _$("#videoPlayer").remove();

    this.imagePreview = new ImagePreview({
      previewInfo: sliderInfo,
      parent: content,
    });
    const imagePreview = this.imagePreview;
    const img = imagePreview.image;
    this.media = img;

    this.createControlBar(mediaInfo.type);
    this.controlBarEvents();
    _$("#zoomIn").on("click", () => imagePreview.zoomIn());
    _$("#zoomOut").on("click", () => imagePreview.zoomOut());
    _$("#leftRotate").on("click", () => imagePreview.rotateImage("left"));
    _$("#rightRotate").on("click", () => imagePreview.rotateImage("right"));

    // Add scroll event listener
    _$(content).on("wheel", this.scrollHandler);
  };

  scrollHandler(e) {
    e.preventDefault();
    if (this.imagePreview) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoomFactor = this.zoomFactor + delta;

      if (
        (delta > 0 && newZoomFactor > this.zoomFactor) ||
        (delta < 0 && newZoomFactor < this.zoomFactor)
      ) {
        this.zoomFactor = Math.max(
          this.minZoom,
          Math.min(this.maxZoom, newZoomFactor)
        );
        this.imagePreview.setZoom(this.zoomFactor);
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  renderVideoQuickView = (sliderInfo) => {
    const content = _$(`#${this.id} .quick-view-player-section`).el();
    const { mediaInfo } = sliderInfo;
    const canShowDownloadButtonInQuickPreview = utils.getUserCustomizationValue(
      "showDownloadButtonInQuickPreview"
    );

    this.videoPreview = new VideoPreview({
      previewInfo: sliderInfo,
      parent: content,
      controls: true,
      disableContextMenu: !canShowDownloadButtonInQuickPreview,
      autoplay: utils.getUserCustomizationValue(
        "autoStartMediaPlaybackInPreview"
      ),
    });
    const videoPreview = this.videoPreview;
    const video = videoPreview.player;
    this.media = video;
    video.on("loadedmetadata", () => {
      this.setMediaPlayerVolume(video);
    });
    video.on("volumechange", () => {
      this.storeMediaPlayerVolume(video);
    });
    // _$(video).on("loadedmetadata", (e) => {
    //   this.setMediaPlayerVolume(video);
    // });

    _$("#videoPlayer").remove();
    // const mediaBar = new MediaBar(video, _$(`#${this.id} #content`).el());
    // mediaBar.init();

    this.createControlBar(mediaInfo.type);
    this.controlBarEvents();
  };

  renderAudioQuickView = (sliderInfo) => {
    const content = _$(`#${this.id} .quick-view-player-section`).el();
    const { mediaInfo } = sliderInfo;

    const audioPreview = new AudioPreview({
      previewInfo: sliderInfo,
      parent: content,
    });
    const audio = audioPreview.audio;
    const waveContainer = audioPreview.waveContainer;
    _$(waveContainer).addClass("wave-div");

    this.media = audio;

    audio.autoplay = true;

    const mediaBar = new MediaBar(audio, audio);
    mediaBar.init();

    const autoNextAudio = utils.getUserCustomizationValue("auto_next_audio");
    if (autoNextAudio) {
      _$(audio).on("ended", () => {
        this.next();
      });
    }

    this.createControlBar(mediaInfo.type);
    this.controlBarEvents();
  };

  setMediaPlayerVolume = (media) => {
    const username = store?.userInfo?.username;
    if (!username) return;

    const volume = localStorage.getItem(`${username}_volume_level`);
    if (!volume) return;
    media.volume(parseFloat(volume));
  };

  storeMediaPlayerVolume = (media) => {
    if (this.storeMediaVolumeTimeoutId)
      clearTimeout(this.storeMediaVolumeTimeoutId);

    const username = store?.userInfo?.username;
    if (username) {
      this.storeMediaVolumeTimeoutId = setTimeout(() => {
        localStorage.setItem(`${username}_volume_level`, media.volume());
      }, 500);
    }
  };

  createControlBar = (mediaType) => {
    const controlBar = _$(
      `#${this.id} .quick-view-footer .control-bar.info-bar`
    ).el();
    const canHideRotateIconsInPreview = utils.getUserCustomizationValue(
      "hideRotateIconsInPreview"
    );

    const canShowDownloadButtonInQuickPreview = utils.getUserCustomizationValue(
      "showDownloadButtonInQuickPreview"
    );
    const canShowAddToBasketButtonInQuickPreview =
      utils.getUserCustomizationValue("showAddToBasketButtonInQuickPreview");
    const canShowQuickShareButtonInQuickPreview =
      utils.getUserCustomizationValue("showQuickShareButtonInQuickPreview");
    const canShowKeywordButtonInQuickPreview = utils.getUserCustomizationValue(
      "showKeywordButtonInQuickPreview"
    );

    const controlBarHTML = templates.controlBarTemplate({
      mediaType,
      canHideRotateIconsInPreview,
      canShowDownloadButtonInQuickPreview,
      canShowAddToBasketButtonInQuickPreview,
      canShowQuickShareButtonInQuickPreview,
      canShowKeywordButtonInQuickPreview,
    });

    _$(controlBar).html(controlBarHTML);

    const rightButton = _$("#right-angle").el();
    const leftButton = _$("#left-angle").el();

    rightButton.disabled = leftButton.disabled = this.mediaArray.length === 1;

    _$(controlBar)
      .find("[data-tooltip]")
      .sel()
      .forEach((button) => {
        this.tooltip.addTooltip({ element: button });
      });
  };

  controlBarEvents = () => {
    const file = this.sliderInfo;

    _$("#right-angle").on("click", () => {
      this.next();
    });

    _$("#left-angle").on("click", () => {
      this.previous();
    });

    _$("#download-quick-view").on("click", () => {
      userActions.invoke({
        name: "download",
        curPath: file.href_path,
        loadedPath: file.root_dir,
        context: file,
      });
    });

    _$("#add-to-basket-quick-view").on("click", () => {
      this.filesAddedToBasket.add(file);
    });

    _$("#edit-keywords-quick-view").on("click", () => {
      userActions.invoke({
        name: "updateKeywords",
        curPath: file.href_path,
        loadedPath: file.root_dir,
        context: file,
      });
    });

    _$("#quick-share-quick-view").on("click", () => {
      userActions.invoke({
        name: "quickShare",
        context: file,
      });
    });
  };

  keyUpHandler = (e) => {
    const mediaTag = this.media.tagName;
    let video;
    if (this.videoPreview) {
      video = this.videoPreview.player;
    }
    if (e.key === "ArrowRight") {
      if (e.altKey && video) {
        video.currentTime += 10;
      } else this.next();
    } else if (e.key === "ArrowLeft") {
      if (e.altKey && video) {
        video.currentTime -= 10;
      } else this.previous();
    } else if (e.key === "Escape") {
      this.closeSlider();
    } else if (e.key === "ArrowUp") {
      if (video) {
        if (video.volume() < 1) {
          video.volume(video.volume() + 0.1);
        }
        this.storeMediaPlayerVolume(video);
      }
    } else if (e.key === "ArrowDown") {
      if (video) {
        if (video.volume() > 0) {
          const vol = video.volume() - 0.1;
          if (vol >= 0) video.volume(vol);
          this.storeMediaPlayerVolume(video);
        }
      }
    } else if (e.key === "-" || e.key === "_") {
      if (this.imagePreview) this.imagePreview.zoomOut(mediaTag);
    } else if (e.key === "+" || e.key === "=") {
      if (this.imagePreview) this.imagePreview.zoomIn(mediaTag);
    } else if (e.key === "0" || e.key === ")") {
      if (this.imagePreview) this.imagePreview.resetZoom(mediaTag);
    } else if (e.key === "l") {
      if (this.imagePreview) this.imagePreview.rotateImage("left");
    } else if (e.key === "r") {
      if (this.imagePreview) this.imagePreview.rotateImage("right");
    } else if (e.keyCode === 32) {
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    } else if (e.key === "m") {
      if (video) {
        if (video.volume() !== 0) {
          video.volume(0);
        } else {
          video.volume(1);
        }
        this.storeMediaPlayerVolume(video);
      }
    } else if (e.key === "f") {
      if (video) {
        if (!document.fullscreenElement) {
          video.requestFullscreen?.() ||
            video.mozRequestFullScreen?.() ||
            video.webkitRequestFullscreen?.() ||
            video.msRequestFullscreen?.();
        } else {
          document.exitFullscreen?.() ||
            document.mozCancelFullScreen?.() ||
            document.webkitExitFullscreen?.() ||
            document.msExitFullscreen?.();
        }
      }
    }
  };

  closeSlider = () => {
    removeEventListener("keyup", this.keyUpHandler);

    _$(`#${this.id}`).remove();
    _$("html").removeClass("crushftp-opened-modal");

    const filesAddedToBasket = Array.from(this.filesAddedToBasket);

    if (filesAddedToBasket.length > 0) {
      const { files: basketFiles } = store.basketDetails;

      const doesFileExists = (file) => {
        return basketFiles.find(
          (curFile) => curFile.href_path === file.href_path
        );
      };

      const filesToAdd = filesAddedToBasket.filter(
        (file) => !doesFileExists(file)
      );

      store.dispatch(
        "addFilesToBasket",
        filesToAdd.map((file) => ({
          ...file,
          isChecked: false,
          paddingLeft: null,
          ariaExpanded: false,
        }))
      );
      this.filesAddedToBasket = new Set([]);
    }

    // Remove scroll event listener
    _$(`#${this.id} #content`).off("wheel", this.scrollHandler);
  };

  updateSliderMediaArray = ({ files }) => {
    this.mediaArray = files;
  };
}
